/**
 * useShellAuth - who is signed in, which session they are in, and what they may rejoin.
 *
 * Auth goes through the backend port; nothing here knows which backend is
 * behind it. On sign-in the display name is taken from the account, and a
 * STORED session is offered for rejoining only if the server still knows it
 * - under the server's live GM flag and join code, not the stored ones; a
 * session that is gone is forgotten. On sign-out the session and the offer
 * clear. A sign-in the server stopped honouring (a redeploy rotated the
 * token, or it expired) is mended only by signing in again, so the app says
 * so and signs out. Extracted from App.
 */
import { useState, useEffect } from 'react';
import { backend } from '../backend/app';
import { getStoredSession, clearStoredSession } from '../utils/sessionStorage';
import { diag, errorSummary, warnIfRejected } from '../diagnostics/log';
import { setAuthExpiredHandler } from '../multiplayer';

/** Nobody's session - a fresh object, so setting it always re-renders as it did. */
export function noSession() {
  return { id: null, code: null, isGm: false };
}

/** A stored session as a rejoin offer, under the server's live GM flag and code. */
export function rejoinOffer(stored, meta) {
  return { ...stored, isGm: meta.isGm, code: meta.joinCode };
}

/**
 * Offer the stored session if the server still has it; forget it if not.
 * A server that cannot be asked (offline) is not a "no": the stored
 * session is kept for the next sign-in, and the failure is a warning.
 */
function offerStoredSession({ setPendingRejoin, setDisplayName }) {
  const stored = getStoredSession();
  if (!stored) return;
  const offered = backend.sessions.validate(stored.id).then((meta) => {
    if (!meta) {
      clearStoredSession();
      return;
    }
    setPendingRejoin(rejoinOffer(stored, meta));
    if (stored.displayName) setDisplayName(stored.displayName);
  });
  warnIfRejected(offered, 'auth', 'stored-session check');
}

/** One auth change: record it, and sign the seat in or out. */
function onAuthChange(u, set) {
  diag.setContext({ uid: u?.uid ?? null });
  diag.info('auth', u ? `signed in as ${u.uid}${u.canHost === false ? ' (guest)' : ''}` : 'signed out');
  set.setUser(u);
  set.setLoading(false);
  if (!u) {
    set.setSession(noSession());
    set.setPendingRejoin(null);
    return;
  }
  // The account's name. (The listener is installed once, so the name typed
  // so far was always the initial '' here - this always applied.)
  set.setDisplayName(u.displayName || '');
  offerStoredSession(set);
}

/** Sign out, loudly, when the server stops honouring the sign-in. */
function useAuthExpiry(pushToast) {
  useEffect(() => {
    setAuthExpiredHandler(() => {
      pushToast('Your sign-in has expired - please sign in again.', { type: 'error', duration: 8000 });
      backend.auth.signOut().catch((err) => diag.error('auth', 'sign-out after expiry failed', errorSummary(err)));
    });
    return () => setAuthExpiredHandler(null);
  }, [pushToast]);
}

/**
 * @param {Function} pushToast
 * @returns {{ user, loading, session, setSession, pendingRejoin, setPendingRejoin, displayName, setDisplayName }}
 */
export default function useShellAuth(pushToast) {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(noSession);
  const [loading, setLoading] = useState(true);
  const [displayName, setDisplayName] = useState('');
  const [pendingRejoin, setPendingRejoin] = useState(null);
  useEffect(() => {
    const set = { setUser, setLoading, setSession, setPendingRejoin, setDisplayName };
    const unsub = backend.auth.onChange((u) => onAuthChange(u, set));
    return () => unsub();
  }, []);
  useAuthExpiry(pushToast);
  return { user, loading, session, setSession, pendingRejoin, setPendingRejoin, displayName, setDisplayName };
}
